/**
 * AI 成效頁的資料來源：`GET /api/admin/ai/observability?range=…`（worker `ai-observability.ts`）。
 *
 * 為什麼不掛在 `@chat-rea/console-ui` 的 `ApiClient` 上：那支介面要同步補 http／mock 兩份
 * 實作，而 mock 版本只會再長一份假數字出來（這次要拿掉的就是假數字）。這頁直接打端點，
 * 憑證規則跟 console-ui 的 `adminRequest` 同一條：session 走 `vmSessionHeaders()`，
 * sessionStorage 有 token 才加 `x-admin-token`。沒有 token 仍打後端。
 *
 * 錯誤一律翻成「人話」＋分類（`ai-observability-failure.ts` 的 `AiObsFailureKind`）再往上丟：
 * 只有 401/403 會是 `unauthorized`，5xx／斷網是 `offline`，400/422 是 `invalid`。
 * 呼叫端（`AiObservabilityPage`）依 `error.kind` 決定要顯示哪一種狀態；不要把所有失敗
 * 都丟成同一個 Error，那會讓「讀取失敗」看起來像「沒有權限」。
 */
import { API_BASE } from '../api';
import { vmSessionHeaders } from '../auth/session';
import {
  AiObsError,
  failureFromNetwork,
  failureFromPayload,
  failureFromResponse,
} from './ai-observability-failure';

export type AiObsRange = '24h' | '7d' | '30d';

/** 端點回應（形狀由 worker 的 `AiObsResponse` 定義；這裡只宣告用到的欄位）。 */
export interface AiObsResponse {
  range: AiObsRange;
  bucket: 'hour' | 'day';
  from: string;
  to: string;
  empty: boolean;
  stats: {
    replies: number;
    costUsd: number;
    avgLatencyMs: number | null;
    handoffs: number;
    handoffRate: number | null;
    deltas: {
      replies: number | null;
      costUsd: number | null;
      avgLatencyMs: number | null;
      handoffRatePt: number | null;
    };
  };
  trend: { labels: string[]; replies: number[]; handoffs: number[] };
  reasons: Array<{ id: string; label: string; value: number; share: number }>;
  versions: {
    a: { id: string; label: string; meta?: string };
    b: { id: string; label: string; meta?: string };
    metrics: Array<{ id: string; label: string; a: number; b: number; unit?: string; invert?: boolean }>;
    summary: string;
  };
  notes: string[];
  generatedAt: string;
}

/**
 * 與 `@chat-rea/console-ui` 的 `ADMIN_TOKEN_STORAGE_KEY`（`api/client.ts`）**同一把 key**。
 * 為什麼不 import：那支常數沒有出現在 `dist/index.d.ts`（subpath export 也鎖住），
 * 為了讀一個字串去重建整個 console-ui 的 dist 不划算；key 名一改兩邊都要改，這裡註明來源。
 * 存在 sessionStorage，關分頁即失效（DESIGN-admin §5.3）。
 */
const ADMIN_TOKEN_STORAGE_KEY = 'chat-rea.admin.token';

function adminToken(): string {
  try {
    return (sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY) ?? '').trim();
  } catch {
    return '';
  }
}

export async function fetchAiObservability(range: AiObsRange, signal?: AbortSignal): Promise<AiObsResponse> {
  const base = API_BASE.trim().replace(/\/+$/, '');
  if (base === '') {
    // 這是這個 console 的設定問題，不是使用者的權限問題，也不是回應格式壞掉。
    throw new AiObsError({
      kind: 'error',
      status: null,
      message: '本機 mock 沒有接 AI 成效 API。要看真數字，開發時設 VITE_API_BASE。',
    });
  }
  const token = adminToken();
  const headers: Record<string, string> = { accept: 'application/json', ...vmSessionHeaders() };
  if (token !== '') headers['x-admin-token'] = token;

  const url = `${base}/api/admin/ai/observability?range=${encodeURIComponent(range)}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'GET',
      headers,
      cache: 'no-store',
      credentials: 'omit',
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new AiObsError(failureFromNetwork(err));
  }

  const text = await res.text();
  let body: unknown;
  try {
    body = text === '' ? undefined : JSON.parse(text);
  } catch {
    body = undefined;
  }

  if (!res.ok) {
    // 分類在 `ai-observability-failure.ts`：401/403→unauthorized、400/422→invalid、5xx→offline。
    throw new AiObsError(failureFromResponse(res.status, body, res.statusText));
  }

  const parsed = body as Partial<AiObsResponse> | undefined;
  if (parsed === undefined || typeof parsed !== 'object' || parsed.trend === undefined || parsed.stats === undefined) {
    throw new AiObsError(failureFromPayload('stats／trend'));
  }
  return parsed as AiObsResponse;
}
